import { useState } from 'react';
import type React from 'react';
import type { MouseEvent } from 'react';

import { LinkOffRounded } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { OrdDatagrid, OrdIconStandard, OrdList } from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { QueueFields } from '@ragu-components/ord-types';
import {
  Confirm,
  FunctionField,
  ResourceContextProvider,
  TextField,
  WrapperField,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
  useUpdate,
} from 'react-admin';

import type { OrdListProps } from '@ragu-components/ord-components';
import type { Queue } from '@ragu-components/ord-types';

const UnassignQueueButton = (): React.ReactElement => {
  const record = useRecordContext<Queue>();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const [update, { isPending }] = useUpdate(
    Resources.queues,
    { id: record?.id, data: { call_recording_conf_id: null }, previousData: record },
    {
      onSuccess: () => {
        notify('ra.notification.updated', { type: 'success', messageArgs: { smart_count: 1 } });
        refresh();
      },
      onError: () => {
        notify('ra.notification.http_error', { type: 'error' });
      },
    }
  );

  if (record == null) return <></>;

  const handleOpen = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    setOpen(true);
  };

  const handleConfirm = (): void => {
    setOpen(false);
    void update();
  };

  return (
    <>
      <Tooltip title={translate('resources.call_recording_confs.confirmUnassignQueue.title', { _: 'Unassign queue' })}>
        <span>
          <IconButton size='small' color='primary' onClick={handleOpen} disabled={isPending}>
            <LinkOffRounded fontSize='small' />
          </IconButton>
        </span>
      </Tooltip>
      <Confirm
        isOpen={open}
        title='resources.call_recording_confs.confirmUnassignQueue.title'
        content='resources.call_recording_confs.confirmUnassignQueue.content'
        onConfirm={handleConfirm}
        onClose={() => {
          setOpen(false);
        }}
      />
    </>
  );
};

export const QueueCallRecordingConfsEmbeddedList = ({
  confId,
  ...props
}: { confId: string | number } & Partial<OrdListProps>): React.ReactElement => {
  const translate = useTranslate();
  const resource = `call_recording_confs/${String(confId)}/queues`;

  return (
    <ResourceContextProvider value={Resources.call_recording_confs}>
      <OrdList
        resource={resource}
        title={translate('resources.queues.name', { _: 'Queues' })}
        icon={<OrdIconStandard name='queues' />}
        hasCreate={false}
        hasFilters={false}
        disableAuthentication
        showTabTitle={false}
        sort={{ field: QueueFields.name, order: 'ASC' }}
        {...props}
      >
        <OrdDatagrid bulkActionButtons={false} rowClick={false}>
          <TextField source={QueueFields.name} label='resources.queues.fields.name' sortable={false} />
          <FunctionField<Queue>
            label='resources.queues.fields.number'
            source={QueueFields.number}
            sortable={false}
            render={(record) => record.number ?? ''}
          />
          <WrapperField label={false}>
            <UnassignQueueButton />
          </WrapperField>
        </OrdDatagrid>
      </OrdList>
    </ResourceContextProvider>
  );
};

export default QueueCallRecordingConfsEmbeddedList;
